/* Dependency Imports */
import { useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";

/* Project Imports */
import { SettingContainer } from "../../commonStyles";
import { IProject } from "../../types/project";
import { IUser } from "../../types/user";
import { useAppDispatch } from "../../app/hooks";
import { showSuccessSnackbar } from "../../features/snackbar/snackbarSlice";
import { selectBrokerBayCc, selectBrokerbayRep, selectCrmProjectId } from "../../features/projectSetting/projectSettingSlice";

const Brokerbay = ({ project }: BrokerbayProps) => {
  const storeDispatch = useAppDispatch();
  const crmProjectId = useSelector(selectCrmProjectId);
  const crmBrokerbayRep = useSelector(selectBrokerbayRep);
  const crmBrokerBayCc = useSelector(selectBrokerBayCc);

  const [users, setUsers] = useState<IUser[]>([]);
  const [salesRep, setSalesRep] = useState<IUser | null>(crmBrokerbayRep);
  const [ccEmails, setCcEmails] = useState<string>(crmBrokerBayCc || "");

  useQuery(GETUSERS, {
    variables: { filter: { OR: [{ type: "Manager" }, { type: "Developer" }, { type: "Sales"}], locked: false } },
    onCompleted: (data) => {
      setUsers(data.userMany);
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const [updateCrmProject] = useMutation(UPDATECRMPROJECT, {
    onCompleted: () => {
      storeDispatch(showSuccessSnackbar("BrokerBay Settings Updated!"));
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const saveBrokerbaySettings = () => {
    updateCrmProject({
      variables: {
        _id: crmProjectId,
        record: {
          brokerbayRep: salesRep?._id,
          brokerBayCc: ccEmails,
        },
      },
    });
  };

  return (
    <SettingContainer>
      <Typography sx={{ mb: 3 }} variant="h2" gutterBottom>
        <strong>BrokerBay</strong>
      </Typography>
      <Box sx={{ my: 2 }}>
        <Typography sx={{ mb: 2 }} variant="h2" gutterBottom>
          <strong>Sales Rep</strong>
        </Typography>
        <Autocomplete
          id="brokerbay-sales-rep"
          options={users}
          freeSolo={false}
          value={salesRep}
          getOptionLabel={(option: any) => option.fullName}
          onChange={(_e, value) => setSalesRep(value)}
          renderInput={(params) => <TextField {...params} variant="outlined" label="Sales Rep" />}
        />
      </Box>
      <Box sx={{ my: 2 }}>
        <Typography sx={{ mb: 2 }} variant="h2" gutterBottom>
          <strong>CC Emails</strong>
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          label="CC Emails (comma separated)"
          placeholder="jeffl@rdsre.ca, jlee123@rdsre.ca"
          value={ccEmails}
          onChange={(e) => setCcEmails(e.target.value)}
        />
      </Box>
      <Button sx={{ mt: 2 }} variant="contained" onClick={() => saveBrokerbaySettings()} color="success">
        Save
      </Button>
    </SettingContainer>
  );
};

/* Types */
type BrokerbayProps = {
  project: IProject | null;
};

const GETUSERS = gql`
  query userMany($filter: FilterFindManyUserInput!) {
    userMany(filter: $filter) {
      _id
      fullName
    }
  }
`;

const UPDATECRMPROJECT = gql`
  mutation crmProjectSettingUpdateById($_id: MongoID!, $record: UpdateByIdCrmProjectSettingInput!) {
    crmProjectSettingUpdateById(_id: $_id, record: $record) {
      record {
        _id
        brokerbayRep {
          _id
          fullName
        }
        brokerBayCc
      }
    }
  }
`;

export default Brokerbay;
